import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MutableRefObject, type RefObject } from "react";
import type { HolidayMeetingConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { HolidayLessonModal } from "../ui/HolidayLessonModal";
import {
  LinkedInInspect,
  MarcInspect,
  QuestionInspect,
  SarahInspect,
  TreasuryInspect,
} from "./HolidayVerifyLab";

type MeetingStage = "invite" | "call" | "pressure" | "investigate" | "decision";
type DialogueLine = NonNullable<HolidayMeetingConfig["dialogue"]>[number];

function collectClipUrls(config: HolidayMeetingConfig): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();
  const add = (url?: string) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    urls.push(url);
  };
  for (const line of [...config.dialogue, ...(config.pressureLines ?? [])]) add(line.clipUrl);
  for (const person of config.participants) add(person.idleClipUrl);
  return urls;
}

function speakInstructions(line: DialogueLine): string {
  return line.speaker === "Elena"
    ? "Natural finance leader on a video call, alert but professional."
    : "Natural executive video call, restrained and confidential.";
}

function speakVoice(line: DialogueLine): string {
  return line.voiceId ?? (line.speaker === "Elena" ? "nova" : "echo");
}

/** Off-screen but still decodable (display:none skips preload in Chromium). */

function ClipHost({
  url,
  poolRef,
  dumpRef,
  onEnded,
  onError,
}: {
  url: string;
  poolRef: MutableRefObject<Map<string, HTMLVideoElement>>;
  dumpRef: RefObject<HTMLDivElement | null>;
  onEnded: () => void;
  onError: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    let started = false;
    let el: HTMLVideoElement | undefined = poolRef.current.get(url);
    let poll: number | null = null;
    let watchdog: number | null = null;
    let handleEnded: (() => void) | null = null;
    let handleError: (() => void) | null = null;
    let handleCanPlay: (() => void) | null = null;

    const start = () => {
      if (cancelled || started || !el) return;
      started = true;
      el.muted = false;
      el.loop = false;
      void el.play().catch(() => {
        if (!cancelled) onError();
      });
    };

    const attach = (video: HTMLVideoElement) => {
      if (cancelled) return;
      el = video;
      handleEnded = () => {
        if (!cancelled) onEnded();
      };
      handleError = () => {
        if (!cancelled) onError();
      };
      handleCanPlay = () => start();
      video.addEventListener("ended", handleEnded);
      video.addEventListener("error", handleError);
      host.appendChild(video);
      if (video.currentTime > 0.05) {
        try {
          video.currentTime = 0;
        } catch {
          /* ignore */
        }
      }
      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) start();
      else video.addEventListener("canplay", handleCanPlay, { once: true });
      watchdog = window.setTimeout(() => {
        if (cancelled || started) return;
        started = true;
        onError();
      }, 5000);
    };

    if (el) attach(el);
    else {
      poll = window.setInterval(() => {
        const ready = poolRef.current.get(url);
        if (!ready) return;
        if (poll !== null) window.clearInterval(poll);
        poll = null;
        attach(ready);
      }, 50);
    }

    return () => {
      cancelled = true;
      if (poll !== null) window.clearInterval(poll);
      if (watchdog !== null) window.clearTimeout(watchdog);
      if (!el) return;
      if (handleEnded) el.removeEventListener("ended", handleEnded);
      if (handleError) el.removeEventListener("error", handleError);
      if (handleCanPlay) el.removeEventListener("canplay", handleCanPlay);
      el.pause();
      el.muted = true;
      dumpRef.current?.appendChild(el);
    };
  }, [url, poolRef, dumpRef, onEnded, onError]);

  return <div className="hm-clip-host" ref={hostRef} />;
}

function IdleHost({
  url,
  frozen,
  poolRef,
  dumpRef,
}: {
  url: string;
  frozen: boolean;
  poolRef: MutableRefObject<Map<string, HTMLVideoElement>>;
  dumpRef: RefObject<HTMLDivElement | null>;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const frozenRef = useRef(frozen);
  frozenRef.current = frozen;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    let el: HTMLVideoElement | undefined = poolRef.current.get(url);
    let poll: number | null = null;
    let handleCanPlay: (() => void) | null = null;

    const start = () => {
      if (cancelled || !el) return;
      el.muted = true;
      el.loop = true;
      el.playsInline = true;
      if (frozenRef.current) el.pause();
      else void el.play().catch(() => undefined);
    };

    const attach = (video: HTMLVideoElement) => {
      if (cancelled) return;
      el = video;
      videoRef.current = video;
      handleCanPlay = () => start();
      video.muted = true;
      video.loop = true;
      host.appendChild(video);
      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) start();
      else video.addEventListener("canplay", handleCanPlay, { once: true });
    };

    if (el) attach(el);
    else {
      poll = window.setInterval(() => {
        const ready = poolRef.current.get(url);
        if (!ready) return;
        if (poll !== null) window.clearInterval(poll);
        poll = null;
        attach(ready);
      }, 50);
    }

    return () => {
      cancelled = true;
      if (poll !== null) window.clearInterval(poll);
      if (!el) return;
      if (handleCanPlay) el.removeEventListener("canplay", handleCanPlay);
      el.pause();
      dumpRef.current?.appendChild(el);
      videoRef.current = null;
    };
  }, [url, poolRef, dumpRef]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (frozen) el.pause();
    else void el.play().catch(() => undefined);
  }, [frozen]);

  return <div className="hm-idle-host" ref={hostRef} />;
}

type GlitchMode = "ok" | "blur" | "grain" | "soft";

const GLITCH_MODES: GlitchMode[] = ["blur", "grain", "soft"];

function useUnstableFeed(enabled: boolean, salt: number, sparse: boolean): GlitchMode {
  const [mode, setMode] = useState<GlitchMode>("ok");
  useEffect(() => {
    if (!enabled) {
      setMode("ok");
      return;
    }
    let waitTimer: number | null = null;
    let clearTimer: number | null = null;
    let stopped = false;
    let last: GlitchMode = "ok";
    const schedule = () => {
      if (stopped) return;
      const wait = sparse
        ? 14000 + (salt % 6000) + Math.random() * 12000
        : 8000 + (salt % 4000) + Math.random() * 8000;
      waitTimer = window.setTimeout(() => {
        const pool = GLITCH_MODES.filter((item) => item !== last);
        const next = pool[Math.floor(Math.random() * pool.length)] ?? "grain";
        last = next;
        setMode(next);
        clearTimer = window.setTimeout(() => {
          setMode("ok");
          schedule();
        }, 2800 + Math.random() * 2400);
      }, wait);
    };
    schedule();
    return () => {
      stopped = true;
      if (waitTimer !== null) window.clearTimeout(waitTimer);
      if (clearTimer !== null) window.clearTimeout(clearTimer);
    };
  }, [enabled, salt, sparse]);
  return mode;
}

const EVIDENCE = [
  { id: "marc_video", title: "Inspect Marc", tag: "Video forensics", clue: "Lip-sync anomaly discovered" },
  { id: "sarah_video", title: "Inspect Sarah", tag: "Video forensics", clue: "Mouth and shoulders disagree" },
  { id: "question", title: "Challenge them", tag: "Knowledge", clue: "A public fact is not identity" },
  { id: "linkedin", title: "Check LinkedIn", tag: "Identity", clue: "Profiles exist. The call does not." },
  { id: "treasury", title: "Inspect room", tag: "Environment", clue: "The whole room can be generated" },
] as const;

function MeetingTile({
  person,
  isSpeaking,
  speakingClipUrl,
  poolRef,
  dumpRef,
  onClipEnded,
  onClipError,
  unstable,
  sparse,
}: {
  person: HolidayMeetingConfig["participants"][number];
  isSpeaking: boolean;
  speakingClipUrl?: string;
  poolRef: MutableRefObject<Map<string, HTMLVideoElement>>;
  dumpRef: RefObject<HTMLDivElement | null>;
  onClipEnded: () => void;
  onClipError: () => void;
  unstable: boolean;
  sparse?: boolean;
}) {
  const salt = person.id.charCodeAt(0) * 37 + person.id.length * 80;
  const mode = useUnstableFeed(unstable && person.camera !== "off" && person.camera !== "self", salt, !!sparse);
  const showTalk = isSpeaking && !!speakingClipUrl;

  return (
    <div
      className={`hm-tile hm-glitch-${mode} ${isSpeaking ? "speaking" : ""} ${person.camera === "off" ? "off" : ""} ${person.camera === "self" ? "self" : ""}`}
    >
      {person.camera === "off" ? (
        <div className="hm-initials">{person.name.split(" ").map((x) => x[0]).join("")}</div>
      ) : (
        <div className="hm-feed">
          {person.idleClipUrl ? (
            <IdleHost url={person.idleClipUrl} frozen={false} poolRef={poolRef} dumpRef={dumpRef} />
          ) : person.avatarUrl ? (
            <div className="hm-live-photo">
              <img src={person.avatarUrl} alt="" />
              <i />
            </div>
          ) : (
            <div className="hm-self-camera">
              <span>EF</span>
              <i>Elena's camera</i>
            </div>
          )}
          {showTalk && (
            <div className="hm-talking">
              <ClipHost
                url={speakingClipUrl!}
                poolRef={poolRef}
                dumpRef={dumpRef}
                onEnded={onClipEnded}
                onError={onClipError}
              />
            </div>
          )}
          {mode !== "ok" && <div className="hm-conn" aria-hidden />}
        </div>
      )}
      <span>
        {person.name}
        <small>
          {person.role}
          {person.camera === "self" ? " · You" : ""}
        </small>
      </span>
      {person.camera === "off" && <b>Camera off</b>}
      {isSpeaking && (
        <div className="hm-speaker-live">
          <span className="hm-mic" aria-hidden>
            ●
          </span>
          <span>
            <strong>{person.name}</strong> is speaking
          </span>
          <i />
          <i />
          <i />
        </div>
      )}
      {person.camera === "self" && <em className="hm-you-badge">YOU</em>}
    </div>
  );
}

export function HolidayMeeting({
  config,
  onComplete,
  onVerification,
  onStageChange,
  style,
}: {
  config: HolidayMeetingConfig;
  onComplete: (outcome: "approved" | "rejected" | "procedure") => void;
  onVerification?: (method: "callback" | "treasury") => void;
  onStageChange?: (stage: string) => void;
  style?: CSSProperties;
}) {
  const [stage, setStage] = useState<MeetingStage>(() => {
    try {
      return new URLSearchParams(window.location.search).get("meetingStage") === "investigate"
        ? "investigate"
        : "invite";
    } catch {
      return "invite";
    }
  });
  const [inviteNote, setInviteNote] = useState("");
  const [lineIndex, setLineIndex] = useState(0);
  const [pressureIndex, setPressureIndex] = useState(0);
  const [tool, setTool] = useState<string | null>(null);
  const [checks, setChecks] = useState<Set<string>>(() => new Set());
  const [exposed, setExposed] = useState(false);
  const [victimSpeaking, setVictimSpeaking] = useState(false);
  const advancingRef = useRef(false);
  const advanceTimerRef = useRef<number | null>(null);
  const poolRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const dumpRef = useRef<HTMLDivElement>(null);
  const joinAtRef = useRef<number | null>(null);
  const clipUrls = useMemo(() => collectClipUrls(config), [config.dialogue, config.pressureLines, config.participants]);
  const [fakeLesson, setFakeLesson] = useState(false);
  const [musicOn, setMusicOn] = useState(() => audio.isMusicEnabled());
  const [counterFlash, setCounterFlash] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [skippedLive, setSkippedLive] = useState(false);

  useEffect(() => onStageChange?.(stage), [stage, onStageChange]);

  useEffect(() => {
    const dump = dumpRef.current;
    if (!dump) return;
    const pool = poolRef.current;
    for (const url of clipUrls) {
      if (pool.has(url)) continue;
      const el = document.createElement("video");
      const idle = config.participants.some((p) => p.idleClipUrl === url);
      el.preload = "auto";
      el.playsInline = true;
      el.muted = true;
      el.autoplay = idle;
      el.loop = idle;
      el.setAttribute("playsinline", "");
      el.setAttribute("muted", "");
      el.setAttribute("preload", "auto");
      if (idle) el.setAttribute("loop", "");
      el.src = url;
      pool.set(url, el);
      dump.appendChild(el);
      el.load();
    }
  }, [clipUrls]);

  useEffect(() => {
    if (stage === "invite") {
      joinAtRef.current = null;
      return;
    }
    if (!joinAtRef.current) joinAtRef.current = Date.now();
    const tick = window.setInterval(() => {
      const start = joinAtRef.current ?? Date.now();
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => window.clearInterval(tick);
  }, [stage]);

  useEffect(() => {
    if (checks.size === 0) return;
    setCounterFlash(true);
    const t = window.setTimeout(() => setCounterFlash(false), 700);
    return () => window.clearTimeout(t);
  }, [checks.size]);

  useEffect(() => {
    const lines = [...config.dialogue, ...(config.pressureLines ?? [])];
    for (const line of lines) {
      if (line.clipUrl) continue;
      void audio.prefetchSpeech(line.text, {
        voice: speakVoice(line),
        instructions: speakInstructions(line),
        lang: "en",
      });
    }
  }, [config.dialogue, config.pressureLines]);

  const lines = stage === "pressure" ? config.pressureLines ?? [] : config.dialogue;
  const idx = stage === "pressure" ? pressureIndex : lineIndex;
  const activeLine = lines[Math.min(idx, Math.max(0, lines.length - 1))];
  const dialogueActive = (stage === "call" || stage === "pressure") && !skippedLive;
  const activeParticipant = dialogueActive
    ? config.participants.find((p) => p.name.startsWith(activeLine?.speaker ?? ""))
    : undefined;
  const victimTurn = dialogueActive && activeLine?.speaker === "Elena";

  const transcript = useMemo(() => {
    if (skippedLive || stage === "investigate" || stage === "decision") {
      return [...config.dialogue, ...(config.pressureLines ?? [])];
    }
    if (stage === "call") return config.dialogue.slice(0, lineIndex + 1);
    if (stage === "pressure") return [...config.dialogue, ...(config.pressureLines ?? []).slice(0, pressureIndex + 1)];
    return [...config.dialogue, ...(config.pressureLines ?? [])];
  }, [config.dialogue, config.pressureLines, lineIndex, pressureIndex, skippedLive, stage]);

  const nextLine = useCallback(() => {
    if (skippedLive) return;
    if (stage === "call") {
      if (lineIndex < config.dialogue.length - 1) setLineIndex((x) => x + 1);
      else setStage("pressure");
      return;
    }
    const pressure = config.pressureLines ?? [];
    if (pressureIndex < pressure.length - 1) setPressureIndex((x) => x + 1);
    else setStage("investigate");
  }, [config.dialogue.length, config.pressureLines, lineIndex, pressureIndex, skippedLive, stage]);

  const advanceAfterPause = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    advanceTimerRef.current = window.setTimeout(nextLine, 280);
  }, [nextLine]);

  useEffect(() => {
    advancingRef.current = false;
    setVictimSpeaking(false);
    return () => {
      if (advanceTimerRef.current !== null) window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    };
  }, [activeLine]);

  useEffect(() => {
    if (skippedLive || (stage !== "call" && stage !== "pressure") || !activeLine || activeLine.clipUrl || victimTurn) return;
    let cancelled = false;
    void audio.speak(activeLine.text, {
      voice: speakVoice(activeLine),
      instructions: speakInstructions(activeLine),
      lang: "en",
    }).then(() => {
      if (!cancelled) advanceAfterPause();
    });
    return () => {
      cancelled = true;
    };
  }, [activeLine, advanceAfterPause, skippedLive, stage, victimTurn]);

  const endedRef = useRef(advanceAfterPause);
  endedRef.current = advanceAfterPause;
  const onClipEnded = useCallback(() => endedRef.current(), []);
  const onClipError = useCallback(() => {
    if (!activeLine) {
      endedRef.current();
      return;
    }
    void audio.speak(activeLine.text, {
      voice: speakVoice(activeLine),
      instructions: speakInstructions(activeLine),
      lang: "en",
    }).then(() => endedRef.current());
  }, [activeLine]);

  const speakAsElena = useCallback(() => {
    if (!activeLine || victimSpeaking) return;
    setVictimSpeaking(true);
    void audio.speak(activeLine.text, {
      voice: speakVoice(activeLine),
      instructions: speakInstructions(activeLine),
      lang: "en",
    }).then(advanceAfterPause);
  }, [activeLine, advanceAfterPause, victimSpeaking]);

  useEffect(() => {
    if (skippedLive || !victimTurn || !activeLine || victimSpeaking) return;
    const t = window.setTimeout(() => speakAsElena(), 3000);
    return () => window.clearTimeout(t);
  }, [victimTurn, activeLine, victimSpeaking, skippedLive, speakAsElena]);

  const skipLiveMeeting = useCallback(() => {
    audio.cancelSpeech();
    advancingRef.current = true;
    if (advanceTimerRef.current !== null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    setVictimSpeaking(false);
    setLineIndex(Math.max(0, config.dialogue.length - 1));
    setPressureIndex(Math.max(0, (config.pressureLines ?? []).length - 1));
    setSkippedLive(true);
  }, [config.dialogue.length, config.pressureLines]);

  const investigate = (id: string) => {
    const firstUse = !checks.has(id);
    setTool(id);
    setChecks((old) => new Set(old).add(id));
    if (firstUse && id === "treasury") {
      onVerification?.("treasury");
    }
    if (id === "treasury") setExposed(true);
  };

  const join = () => {
    setInviteNote("");
    joinAtRef.current = Date.now();
    setElapsed(0);
    setStage("call");
  };

  const allInvestigated = checks.size === EVIDENCE.length;
  const livePeople = config.participants.filter((p) => p.camera !== "self");
  const clock = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;

  const toolContent =
    tool === "marc_video" ? (
      <MarcInspect stillUrl={config.participants[0]?.avatarUrl} clipUrl="/cdn/sim/holiday-call/clips/marc-04.mp4" />
    ) : tool === "sarah_video" ? (
      <SarahInspect stillUrl={config.participants[1]?.avatarUrl} clipUrl="/cdn/sim/holiday-call/clips/sarah-01.mp4" />
    ) : tool === "question" ? (
      <QuestionInspect answer={config.personalQuestionAnswer ?? "Da Marco."} />
    ) : tool === "linkedin" ? (
      <LinkedInInspect people={config.participants.slice(0, 3)} />
    ) : tool === "treasury" ? (
      <TreasuryInspect />
    ) : null;

  return (
    <section className={`holiday-meeting hm-stage-${stage}${skippedLive ? " hm-skipped" : ""}${style ? " hm-embedded" : ""}`} style={style}>
      <div className="hm-clip-dump" ref={dumpRef} aria-hidden />
      {stage === "invite" ? (
        <div className="hm-invite">
          <div className="hm-teams-mark">T</div><small>MICROSOFT TEAMS · INCOMING</small>
          <h2>Marc Weber — CFO</h2>
          <p>Sorry to bother you while you're away. Something important came up concerning <strong>{config.project}</strong>. It's highly confidential. Can you join us for five minutes?</p>
          {inviteNote && <div className="hm-invite-note">{inviteNote}</div>}
          <div><button className="hm-join cf-pulse-target" onClick={join}>Join confidential meeting <em className="cf-click-here">Click here</em></button></div>
        </div>
      ) : (
        <>
          <header className="hm-top">
            <div className="hm-teams-mark">T</div>
            <strong>{config.project} · Confidential</strong>
            <em className="hm-link-warn">{stage === "investigate" ? "Connection unstable" : "Unstable connection"}</em>
            {(stage === "call" || stage === "pressure") && !skippedLive && (
              <button type="button" className="hm-skip-live" onClick={skipLiveMeeting}>
                Skip live meeting
              </button>
            )}
            {(stage === "call" || stage === "pressure") && skippedLive && (
              <button
                type="button"
                className="hm-skip-live hm-skip-live-go cf-pulse-target"
                onClick={() => setStage("investigate")}
              >
                Continue to investigation
              </button>
            )}
            <button type="button" className="hm-fake-btn" onClick={() => setFakeLesson(true)}>
              Why this meeting can be fake
            </button>
            <span className="hm-live-clock">
              {stage === "investigate" && <i className="hm-live-dot" />}
              {stage === "investigate" || stage === "decision" ? clock : "00:0" + Math.min(9, idx + 1)}
            </span>
          </header>
          <div className={`hm-call${stage === "investigate" ? " hm-call-live" : ""}`}>
            <div className={`hm-grid${stage === "investigate" ? " hm-grid-live" : ""}`}>
              {(stage === "investigate" ? livePeople : config.participants).map((p) => (
                <MeetingTile
                  key={p.id}
                  person={p}
                  isSpeaking={activeParticipant?.id === p.id}
                  speakingClipUrl={activeParticipant?.id === p.id ? activeLine?.clipUrl : undefined}
                  poolRef={poolRef}
                  dumpRef={dumpRef}
                  onClipEnded={onClipEnded}
                  onClipError={onClipError}
                  unstable={(p.id === "sarah" || p.id === "marc") && (stage === "call" || stage === "pressure" || stage === "investigate")}
                  sparse={p.id === "marc" || stage === "investigate"}
                />
              ))}
            </div>
            {stage !== "investigate" && (
              <aside className={`hm-transcript${skippedLive ? " is-full" : ""}`}>
                <h3>{skippedLive ? "Full conversation" : "Live transcript"}</h3>
                {transcript.map((l, i) => (
                  <p key={`${l.speaker}-${i}`} className={i === transcript.length - 1 ? "current" : ""}>
                    <strong>{l.speaker}</strong>
                    {l.text}
                  </p>
                ))}
              </aside>
            )}
            {stage === "investigate" && (
              <p className="hm-live-quote">“{config.pressureLines?.[config.pressureLines.length - 1]?.text ?? "We need the payment released before the bank closes."}”</p>
            )}
          </div>

          {(stage === "call" || stage === "pressure") && victimTurn && !skippedLive && (
            <div className="hm-victim-turn">
              <div><small>YOUR TURN</small><strong>Elena needs to respond</strong><p>“{activeLine?.text}”</p></div>
              <button className="cf-pulse-target" onClick={speakAsElena} disabled={victimSpeaking}>
                {victimSpeaking ? "Elena is speaking…" : <>Speak as Elena<em className="cf-click-here">Click here</em></>}
              </button>
            </div>
          )}

          {(stage === "pressure" || stage === "decision") && !skippedLive && (
            <div className="hm-payment">
              <small>PAYMENT AUTHORIZATION REQUEST</small><strong>{config.amount}</strong>
              <span>Recipient: {config.recipient}</span><span>Bank: {config.bank}</span><span>Reference: {config.reference}</span>
            </div>
          )}

          {stage === "investigate" && (
            <div className={`hm-evidence${tool ? " has-open-tool" : ""}`}>
              <header>
                <div>
                  <small>INTERACTIVE VERIFICATION CHALLENGE</small>
                  <h2>Something doesn&apos;t feel right.</h2>
                  <p>Use the evidence tools before deciding whether Elena should approve the payment.</p>
                </div>
                <strong className={`hm-evidence-count${counterFlash ? " is-flash" : ""}`}>
                  Evidence {checks.size} / {EVIDENCE.length}
                </strong>
              </header>
              <div className="hm-evidence-work">
                <div className="hm-evidence-card-frame">
                  <div className="hm-evidence-frame-title"><b>Choose an evidence tool</b><span>Open cards in any order</span></div>
                  <div className="hm-evidence-grid">
                    {EVIDENCE.map((item, itemIndex) => {
                      const person = config.participants.find((p) =>
                        item.id === "marc_video" ? p.id === "marc" : item.id === "sarah_video" ? p.id === "sarah" : false,
                      );
                      const checked = checks.has(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          style={{ "--hm-card-delay": `${itemIndex * 140}ms` } as CSSProperties}
                          className={`hm-e-card hm-e-${item.id}${checked ? " is-done" : " is-pending"}`}
                          onClick={() => investigate(item.id)}
                        >
                          <div className="hm-e-thumb" aria-hidden>
                            {item.id === "marc_video" || item.id === "sarah_video" ? (
                              person?.avatarUrl ? <img src={person.avatarUrl} alt="" /> : <span>{item.title[0]}</span>
                            ) : item.id === "question" ? (
                              <div className="hm-e-q">
                                <i />
                                <b>?</b>
                              </div>
                            ) : item.id === "linkedin" ? (
                              <div className="hm-e-li">
                                <em>in</em>
                                <span>
                                  <b>{config.participants[0]?.name ?? "Marc Weber"}</b>
                                  <small>{config.participants[0]?.role ?? "CFO"}</small>
                                </span>
                              </div>
                            ) : (
                              <div className="hm-e-room">
                                <i /><i /><i />
                              </div>
                            )}
                          </div>
                          <strong>{checked ? `✓ ${item.title}` : item.title}</strong>
                          <small>{checked ? item.clue : item.tag}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <aside className="hm-evidence-help">
                  <small>WHAT TO DO</small>
                  <h3>Click each card to investigate</h3>
                  <p>Inspect the video, identity and context clues. Opened cards turn turquoise and retain the clue you discovered.</p>
                  <ol>
                    <li>Choose a card</li>
                    <li>Use its interactive evidence</li>
                    <li>Close it and try another</li>
                  </ol>
                  <b>{checks.size === 0 ? "Start with any glowing card." : `${checks.size} of ${EVIDENCE.length} reviewed.`}</b>
                </aside>
              </div>
              {tool && <div className="hm-modal hv-modal"><button className="hm-close" onClick={() => setTool(null)}>Close evidence</button>{toolContent}</div>}
              {exposed && <div className="hm-alert">The meeting grid can be entirely generated. You still control the final payment decision.</div>}
              <footer className="hm-evidence-foot">
                <button
                  type="button"
                  className={`hm-audio-tog${musicOn ? " is-on" : ""}`}
                  onClick={() => {
                    const next = !musicOn;
                    audio.setMusicEnabled(next);
                    setMusicOn(next);
                  }}
                >
                  Audio {musicOn ? "on" : "off"}
                </button>
                <button type="button" className="hm-help" onClick={() => setFakeLesson(true)}>Need help?</button>
                <button
                  type="button"
                  className={`hm-pay-cta cf-pulse-target${allInvestigated ? " ready" : ""}`}
                  onClick={() => setStage("decision")}
                >
                  Continue: make the payment decision <em className="ht-click-chip">Click here</em>
                </button>
              </footer>
            </div>
          )}

          {stage === "decision" && (
            <div className="hm-decision">
              <small>FINAL DECISION</small><h2>{config.amount}</h2><p>{exposed ? "You independently exposed the fake meeting." : "The meeting is convincing, but the request is outside the normal authorization process."}</p>
              <div><button className="danger" onClick={() => onComplete("approved")}>Approve payment</button><button onClick={() => onComplete("rejected")}>Reject and report</button><button className="safe cf-pulse-target" onClick={() => onComplete("procedure")}>Verify through company payment procedure<em className="cf-click-here">Click here</em></button></div>
              <button className="hm-back" onClick={() => setStage("investigate")}>Investigate another signal</button>
            </div>
          )}
          {fakeLesson && (
            <HolidayLessonModal
              kind="meeting"
              title="The Entire Meeting Can Be Fake"
              soft
              paragraphs={[
                "A video meeting is no longer proof that the people you see and hear are really there. Attackers can combine several techniques:",
                "Live deepfake: A real attacker speaks and reacts normally, while AI replaces their face with someone else's and changes their voice in real time.",
                "AI-generated impersonation: AI can generate a cloned voice, synthetic face and increasingly conversational responses using information collected about the person, company and situation.",
                "Staged meeting: Some \"participants\" may be prerecorded deepfakes, others may use cloned voices, and others may simply appear with their camera off. Several familiar colleagues agreeing with each other can therefore all be part of the same attack.",
                "This is not theoretical. In 2024, a Hong Kong employee authorized around HK$200 million (US$25 million) in transfers after joining a video conference supposedly attended by the company CFO and several colleagues. Police concluded that the other participants were fabricated using publicly available video and audio. The FBI also reports seeing synthetic voices and deepfakes used in Teams and Zoom calls to convince employees to make payments.",
                "The lesson: Do not treat faces, voices, caller names—or even several colleagues agreeing in the same meeting—as independent verification. For payments, credentials, confidential information or unusual requests, verify outside the meeting through a known, trusted channel.",
              ]}
              onClose={() => setFakeLesson(false)}
            />
          )}
        </>
      )}
    </section>
  );
}
